import { useQuery } from 'react-query';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

/**
 * Cross-group opener descalce snapshot — read from
 * `manual-order-monitor`'s sweeper via `prediction-arbitrage-manager`'s
 * `GET /positions/opener-descalce` (a single Redis GET, no Mongo, no venue
 * call). Poll pattern copied from `api/useOpenerGroupPods.ts`.
 *
 * Wire contract per the approved spec/plan (§3.2). `ruleStatus` is typed as
 * plain `string`, NOT a union: the live `entry_rules` collection only ever
 * carries `active` / `paused` today, and typing a union the DB doesn't back
 * would let the FE assume `completed` / `cancelled` rows can't appear.
 */

export type OpenerDescalceAccountKey = 'prod' | 'test';

export type OpenerDescalceSource =
  | 'venue'
  | 'starting'
  | 'unconfigured'
  | 'error';

export interface OpenerDescalceAccountStatus {
  source: OpenerDescalceSource;
  lastSuccessAt: number | null;
  lastError: string | null;
  redeemableExcluded: number;
}

export interface OpenerDescalceGroup {
  id: string | null;
  name: string | null;
  podsDesired: 'on' | 'off' | 'panicked' | null;
  status: string | null;
  testAccount: boolean;
  eventFinished: boolean;
}

export interface OpenerDescalceVenue {
  polyYes: number;
  polyNo: number;
  kalshiYes: number;
  kalshiNo: number;
  /** `+` = excess YES. */
  signedNet: number;
  /** `|signedNet|`. */
  descalce: number;
  /** More than one rule claims one of this row's markets. */
  ambiguous: boolean;
  /** A leg is redeemable / already settled — not a naked leg to hedge. */
  resolving: boolean;
  /** This rule is missing a ticker or a Polymarket leg — the counts above
   *  necessarily come from one venue only. Optional: only rows built from a
   *  live backend snapshot carry it; keep it optional so existing fixtures
   *  that predate this field don't need updating. */
  marketsIncomplete?: boolean;
}

export interface OpenerDescalceBelief {
  kalshiNet: number;
  polyNet: number;
  descalce: number;
  /** Producer (engine) clock — passed through verbatim, never re-stamped. */
  at: number;
}

export interface OpenerDescalceDivergence {
  total: number;
  kalshi: number;
  poly: number;
}

export interface OpenerDescalceRow {
  ruleId: string;
  pairId: string;
  pairName: string;
  ruleStatus: string;
  direction: 'entry';
  account: OpenerDescalceAccountKey;

  group: OpenerDescalceGroup;
  subgroupId: string | null;

  markets: {
    kalshiTicker: string | null;
    polyYesTokenId: string | null;
    polyNoTokenId: string | null;
  };

  /** `null` when this row's account is unreadable. */
  venue: OpenerDescalceVenue | null;
  /** `null` when the engine has published nothing for this rule. */
  belief: OpenerDescalceBelief | null;
  /** `null` whenever `belief` is `null`. */
  divergence: OpenerDescalceDivergence | null;

  maxUnhedgedContracts: number | null;
  hedgeCheckEnabled: boolean;
}

export interface OpenerDescalceSnapshot {
  /** epoch ms, sweeper clock. */
  computedAt: number;
  /** so the FE can age it. */
  pollMs: number;
  /** freshness horizon. */
  staleMs: number;
  accounts: Record<OpenerDescalceAccountKey, OpenerDescalceAccountStatus>;
  rows: OpenerDescalceRow[];
}

export const OPENER_DESCALCE_SNAPSHOT_UNAVAILABLE_REASON =
  'snapshot-unavailable';

/**
 * Thrown by `fetchOpenerDescalce` when the backend 404s with
 * `{ reason: 'snapshot-unavailable' }` — the sweeper hasn't published yet, or
 * its key expired. Distinguished from every OTHER fetch failure so the page
 * can render the named "snapshot unavailable" state instead of a generic
 * error (never a silent FE fallback to another source).
 */
export class OpenerDescalceSnapshotUnavailableError extends Error {
  constructor() {
    super('opener-descalce snapshot unavailable');
    this.name = 'OpenerDescalceSnapshotUnavailableError';
  }
}

export function isSnapshotUnavailableError(error: unknown): boolean {
  return error instanceof OpenerDescalceSnapshotUnavailableError;
}

export const OPENER_DESCALCE_KEY = ['opener-descalce-snapshot'];

export async function fetchOpenerDescalce(): Promise<OpenerDescalceSnapshot> {
  try {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<OpenerDescalceSnapshot>(
        '/positions/opener-descalce'
      );
    return data;
  } catch (err) {
    const axiosErr = err as {
      response?: { status?: number; data?: { reason?: string } };
    };
    if (
      axiosErr.response?.status === 404 &&
      axiosErr.response?.data?.reason ===
        OPENER_DESCALCE_SNAPSHOT_UNAVAILABLE_REASON
    ) {
      throw new OpenerDescalceSnapshotUnavailableError();
    }
    throw err;
  }
}

/**
 * Poll every 5s (the underlying sweep is 15s regardless of transport — a
 * shorter poll buys nothing, a longer one delays a fresh naked leg showing
 * up). `retry: false` so a 404 (the expected pre-first-sweep state) renders
 * immediately instead of retrying react-query's default 3 times.
 */
export function useOpenerDescalce() {
  return useQuery<OpenerDescalceSnapshot>(
    OPENER_DESCALCE_KEY,
    fetchOpenerDescalce,
    {
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      retry: false
    }
  );
}
